import { computed, inject } from "@angular/core";
import { Auth, User, onAuthStateChanged } from "@angular/fire/auth";
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { Accounts } from "../../api/nice-ops/models/accounts";

export interface AuthState {
    user: User | null;
    account: Accounts | null;
    idToken: string | null;
    error: string | null;
    isInitialized: boolean;
}

const initialState: AuthState = {
    user: null,
    account: null,
    idToken: null,
    error: null,
    isInitialized: false
};

export const AuthStore = signalStore(
    { providedIn: "root" },
    withState(initialState),
    withComputed((store) => ({
        isAuthenticated: computed(() => !!store.user()),
        isLoading: computed(() => !store.isInitialized())
    })),
    withMethods((store) => ({
        setUser(user: User | null, idToken: string | null): void {
            patchState(store, { user, idToken });
        },

        setAccount(account: Accounts | null): void {
            patchState(store, { account });
        },

        setInitialized(isInitialized: boolean): void {
            patchState(store, { isInitialized });
        },

        setError(error: string | null): void {
            patchState(store, { error });
        },

        clearError(): void {
            patchState(store, { error: null });
        },

        reset(): void {
            patchState(store, initialState);
        }
    })),
    withHooks({
        onInit(store) {
            const auth = inject(Auth);

            // Listen to auth state changes from Firebase
            onAuthStateChanged(auth, async (user) => {
                if (user) {
                    try {
                        const idToken = await user.getIdToken();
                        store.setUser(user, idToken);
                        store.setError(null);
                    } catch (error: unknown) {
                        if (error instanceof Error) {
                            console.error("Error getting ID token:", error);
                            store.setUser(user, null);
                            store.setError(error.message);
                        } else {
                            console.error("Error getting ID token:", error);
                            store.setUser(user, null);
                            store.setError("An unknown error occurred");
                        }
                    }
                } else {
                    store.setUser(null, null);
                    store.setAccount(null);
                    store.setError(null);
                }

                store.setInitialized(true);
            });
        }
    })
);
